/**
 * «Plantillas de WhatsApp» en la ficha de un canal Coexistence (change `conversaciones-templates`,
 * Fase 2, D16 a D19): la lista de la tienda con el estado y la categoría que Meta le dio a la copia de
 * este canal, y las acciones que afectan a la definición, o sea a todos los canales de la tienda.
 *
 * Leer pide Viewer en el canal; escribir, Editor. Con un backend sin templates el GET da 404 y la
 * sección no se muestra.
 */
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  theme,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Channel,
  ChannelConnectionStatus,
  TemplateDefinition,
  TemplateItem,
  TemplatePublication,
  TemplateText,
  TemplateVariable,
  TemplatesView,
  conversationsApi,
} from "@/services/conversations.api";
import { LateralModal } from "@/components/modals/LateralModal";
import { TARJETA_CON_ENCABEZADO } from "@/pages/catalog/HistorialDelCatalogo";
import { codigoDeError } from "@/services/channel-types";
import { useOperatorStores } from "@/hooks/useOperatorStores";
import { mensajeDelAlta } from "./embedded-signup";
import {
  RATE_CARD_URL,
  TIPOS_DE_DATO,
  TipoDeDato,
  Tono,
  aplicarPublicacion,
  avisoDeCategoria,
  categoriaDe,
  estadoDe,
  numerosDeVariables,
  renderizar,
  renderizarTienda,
  tipoDeDato,
  validarTemplate,
  variableDelTipo,
  variablesPorDefecto,
} from "./cloud-templates";

const { Text, Link, Paragraph, Title } = Typography;

const COLOR_DEL_TONO: Record<Tono, string> = { ok: "green", espera: "blue", problema: "orange", neutro: "default" };

const ALTO_DE_LA_TABLA = 500;

type Drawer = { modo: "nueva" } | { modo: "version"; def: TemplateDefinition };

export const CloudTemplatesSection = ({
  channel,
  status,
  evento,
}: {
  channel: Channel;
  status: ChannelConnectionStatus;
  /** La última copia que llegó por `channel:templates`, para reflejarla sin recargar. */
  evento: TemplatePublication | null;
}) => {
  const [view, setView] = useState<TemplatesView | null>(null);
  const [disponible, setDisponible] = useState(true);
  const [sinPermiso, setSinPermiso] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<Drawer | null>(null);
  /** La plantilla abierta en el panel, por `definitionId`. */
  const [panel, setPanel] = useState<string | null>(null);
  const { token } = theme.useToken();
  const channelId = channel.channelId;

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    setSinPermiso(false);
    conversationsApi
      .listTemplates(channelId)
      .then((v) => vigente && setView(v))
      .catch((e) => {
        // Un backend sin templates: la sección no existe para este ambiente.
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 404) setDisponible(false);
        // Sin permiso en el canal no es una falla: lo dice la tabla, sin cartel de error (xcons-docs#508).
        else if (status === 403) vigente && setSinPermiso(true);
        else message.error(`No se pudieron cargar las plantillas: ${mensajeDelAlta(e)}`);
      })
      .finally(() => vigente && setCargando(false));
    conversationsApi
      .myChannelLevel(channelId)
      .then((r) => vigente && setCanEdit(r.level === "editor"))
      .catch(() => {});
    return () => {
      vigente = false;
    };
  }, [channelId]);

  useEffect(() => {
    if (evento) setView((v) => (v ? aplicarPublicacion(v, evento) : v));
  }, [evento]);

  const conectado = status === "connected" && Boolean(view?.wabaId);
  const tienda = String(channel.config?.verified_name ?? "").trim() || channel.name;

  const correr = async (clave: string, accion: () => Promise<TemplatesView>, ok?: string): Promise<boolean> => {
    setOcupado(clave);
    try {
      setView(await accion());
      if (ok) message.success(ok);
      return true;
    } catch (e) {
      message.error(mensajeDelAlta(e));
      return false;
    } finally {
      setOcupado(null);
    }
  };

  if (!disponible) return null;

  const columnas = [
    {
      title: "Plantilla",
      key: "nombre",
      width: 220,
      render: (_: unknown, item: TemplateItem) => (
        <Text strong>
          {item.definition.name} <Text type="secondary">v{item.definition.version}</Text>
        </Text>
      ),
    },
    {
      title: "Texto",
      key: "texto",
      render: (_: unknown, item: TemplateItem) => {
        const texto = item.publication?.renderedBody ?? renderizarTienda(item.definition.body, tienda);
        return (
          <Tooltip title={texto}>
            <Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0, maxWidth: 420 }}>
              {texto}
            </Paragraph>
          </Tooltip>
        );
      },
    },
    {
      title: "Estado",
      key: "estado",
      width: 200,
      render: (_: unknown, item: TemplateItem) => {
        const estado = estadoDe(item, conectado);
        const detalle = item.publication?.reason ?? item.publication?.error ?? null;
        return (
          <Space direction="vertical" size={2}>
            <Tag color={COLOR_DEL_TONO[estado.tono]}>{estado.texto}</Tag>
            {detalle && estado.tono === "problema" && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {detalle}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "Categoría",
      key: "categoria",
      width: 150,
      render: (_: unknown, item: TemplateItem) => {
        const cat = categoriaDe(item.publication?.category);
        const aviso = avisoDeCategoria(item.publication);
        if (!cat) return <Text type="secondary">—</Text>;
        return (
          <Space size={4}>
            <Text>{cat.texto}</Text>
            {aviso && (
              <Tooltip title={aviso}>
                <ExclamationCircleFilled style={{ color: "#fa8c16" }} />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Otros canales",
      key: "otros",
      width: 180,
      render: (_: unknown, item: TemplateItem) => {
        // Sólo los canales donde está publicada: "sin publicar" en otro canal no es un uso (xcons-docs#508).
        const publicadas = item.otherChannels.filter((o) => o.status);
        return publicadas.length ? (
          <Space direction="vertical" size={0}>
            {publicadas.map((o) => (
              <Text key={o.channelId} type="secondary" style={{ fontSize: 12 }}>
                {o.name}: {estadoDe({ ...item, publication: { ...(item.publication ?? ({} as TemplatePublication)), status: o.status! } }, true).texto}
              </Text>
            ))}
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
  ];

  // Las acciones viven en el panel de la plantilla, no en una columna (xcons-docs#512). El panel lee
  // la fila de `view`, así refleja lo que cambie mientras está abierto.
  const enPanel = view?.items.find((i) => i.definition.definitionId === panel) ?? null;
  // Una versión más nueva que todavía espera a Meta en este canal: una rechazada o fallida no bloquea la edición.
  // La de la tienda cuenta como más nueva que la del catálogo con ese nombre, sea cual sea el número; al revés, nunca.
  const versionMasNueva = (def: TemplateDefinition) =>
    view?.items.find(
      (i) =>
        i.definition.name === def.name &&
        i.publication !== null &&
        ["pending", "in_review"].includes(i.publication.status) &&
        (i.definition.catalog === def.catalog ? i.definition.version > def.version : def.catalog && !i.definition.catalog),
    )?.definition ?? null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Flex justify="space-between" align="center" gap={12} wrap style={{ marginTop: 32, marginBottom: 24 }}>
        <Flex align="center" gap={8}>
          <Title level={5} style={{ margin: 0 }}>
            Plantillas de WhatsApp
          </Title>
          <Tooltip
            color={token.colorBgElevated}
            overlayInnerStyle={{ color: token.colorText, padding: "10px 12px" }}
            overlayStyle={{ maxWidth: 380 }}
            title={<AyudaDePlantillas colorDelLink={token.colorPrimary} />}
          >
            <InfoCircleOutlined aria-label="Qué son las plantillas" style={{ color: token.colorTextSecondary, cursor: "help" }} />
          </Tooltip>
        </Flex>
        <Space>
          {canEdit && (
            <Button
              size="large"
              icon={<ReloadOutlined />}
              loading={ocupado === "refresh"}
              disabled={!conectado}
              onClick={() => void correr("refresh", () => conversationsApi.refreshTemplates(channelId))}
            >
              Actualizar estado
            </Button>
          )}
          {/* Sin permiso de editor el botón se ve igual, deshabilitado y diciendo por qué (xcons-docs#508). */}
          <Tooltip title={canEdit ? null : "Para crear plantillas necesitás ser editor del canal"}>
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              iconPosition="end"
              disabled={!canEdit}
              onClick={() => setDrawer({ modo: "nueva" })}
            >
              Nueva plantilla
            </Button>
          </Tooltip>
        </Space>
      </Flex>
      <Flex vertical gap={12}>
        {view && !conectado && (
          <AvisoDeMeta titulo="Las plantillas se publican en la cuenta de Meta del número al conectarlo">
            Hasta entonces se ven acá, pero no se pueden mandar.
          </AvisoDeMeta>
        )}
        {view && conectado && view.items.some((i) => !i.publication) && (
          <AvisoDeMeta titulo="Hay plantillas sin publicar en este canal">
            {canEdit
              ? "«Actualizar estado» las publica en la cuenta de Meta del número y las manda a revisión. Meta suele contestar en minutos; el estado se actualiza solo mientras estén en revisión, y también con ese botón."
              : "Las publica un editor del canal con «Actualizar estado»."}
          </AvisoDeMeta>
        )}
        {/* El mismo borde que las tablas de permisos del canal (xcons-docs#508). */}
        <div style={{ border: "1px solid #d9d9d9", borderRadius: 8, overflow: "hidden", backgroundColor: "#fff" }}>
          <Table<TemplateItem>
            size="small"
            className="tabla-al-alto-de-pantalla tabla-en-contenedor"
            // Hasta 500 px la tabla entera: el encabezado más las filas, que scrollean adentro. Es un tope:
            // con pocas plantillas queda corta (xcons-docs#508).
            style={{ ["--alto-filas" as string]: `${ALTO_DE_LA_TABLA - 39}px` }}
            scroll={{ y: ALTO_DE_LA_TABLA - 39 }}
            rowKey={(item) => item.definition.definitionId}
            onRow={(item) => ({
              onClick: () => setPanel(item.definition.definitionId),
              style: { cursor: "pointer" },
              ...(item.definition.catalog ? { title: "Plantilla del catálogo de xcons: al editarla pasa a ser de la tienda" } : {}),
            })}
            loading={cargando}
            dataSource={view?.items ?? []}
            columns={columnas}
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} plantillas`, locale: { items_per_page: "/ página" } }}
            locale={{
              emptyText: sinPermiso
                ? "Necesitás permiso en este canal para ver las plantillas"
                : "Todavía no hay plantillas en este canal",
            }}
          />
        </div>
      </Flex>

      <LateralModal
        hideTrigger
        openDrawer={enPanel !== null}
        closeDrawer={() => setPanel(null)}
        title={enPanel ? `${enPanel.definition.name} v${enPanel.definition.version}` : ""}
        width={560}
      >
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          {enPanel && (
            <TemplatePanel
              item={enPanel}
              tienda={tienda}
              conectado={conectado}
              canEdit={canEdit}
              ocupado={ocupado}
              enRevision={versionMasNueva(enPanel.definition)}
              onRepublicar={(pub) => void correr(pub.publicationId, () => conversationsApi.republishTemplate(channelId, pub.publicationId))}
              onEditar={() => {
                setPanel(null);
                setDrawer({ modo: "version", def: enPanel.definition });
              }}
              onEliminar={() =>
                void correr(
                  enPanel.definition.definitionId,
                  () => conversationsApi.retireTemplate(channelId, enPanel.definition.definitionId),
                  "Plantilla eliminada",
                ).then((ok) => ok && setPanel(null))
              }
            />
          )}
        </div>
      </LateralModal>

      <LateralModal
        hideTrigger
        openDrawer={drawer !== null}
        closeDrawer={() => setDrawer(null)}
        title={drawer?.modo === "version" ? `Editar ${drawer.def.name}` : "Nueva plantilla"}
        width={560}
      >
        {/* Ocupa el alto del panel: el formulario scrollea y el pie con los botones queda fijo abajo. */}
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          {drawer && (
            <TemplateForm
              key={drawer.modo === "version" ? drawer.def.definitionId : "nueva"}
              drawer={drawer}
              tienda={tienda}
              storeCode={channel.storeCode}
              onDone={(v) => {
                setView(v);
                setDrawer(null);
                message.success("Plantilla enviada a revisión de Meta");
              }}
              channelId={channelId}
              onCancel={() => setDrawer(null)}
            />
          )}
        </div>
      </LateralModal>
    </div>
  );
};

/** Lo que antes era el párrafo de arriba de la tabla, ahora en el tooltip del subtítulo (xcons-docs#508). */
const AyudaDePlantillas = ({ colorDelLink }: { colorDelLink: string }) => (
  <Flex vertical gap={6}>
    <span>
      Con una plantilla aprobada por Meta podés escribirle a un contacto aunque hayan pasado más de 24 h desde su
      último mensaje, o empezar una conversación con alguien que tiene ficha.
    </span>
    <span>Meta decide la categoría de cada plantilla, y la puede cambiar después:</span>
    <ul style={{ paddingLeft: 18, margin: 0 }}>
      <li>
        <Text strong>Utility</Text>: el seguimiento de algo que pidió el cliente.
      </li>
      <li>
        <Text strong>Marketing</Text>: la categoría más cara. Meta la asigna cuando ve intención promocional.
      </li>
    </ul>
    <Link href={RATE_CARD_URL} target="_blank" rel="noreferrer" strong style={{ color: colorDelLink }}>
      Lo que cobra Meta por cada una
    </Link>
  </Flex>
);

const AZUL_META = "#0866FF";

/** El logo de Meta: lo que viene de Meta se reconoce de un vistazo. */
const LogoDeMeta = () => (
  <svg viewBox="0 0 24 24" width={18} height={18} fill={AZUL_META} aria-hidden style={{ flexShrink: 0, marginTop: 2 }}>
    <path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z" />
  </svg>
);

/**
 * Lo que dice o diría Meta, con su logo y su azul: se lee como una previsualización de Meta y no como
 * un banner del admin (xcons-docs#508).
 */
const AvisoDeMeta = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <div
    role="note"
    style={{ display: "flex", gap: 10, border: `1px solid ${AZUL_META}`, background: "#F0F6FF", borderRadius: 8, padding: "10px 12px" }}
  >
    <LogoDeMeta />
    <div>
      <Text strong style={{ display: "block" }}>
        {titulo}
      </Text>
      <div style={{ fontSize: 13 }}>{children}</div>
    </div>
  </div>
);

/**
 * El selector de «Tipo de dato» (#519), una lista sin grupos ni texto de quién lo completa: eso lo
 * resuelve el popup de envío, que ya trae lo del contacto.
 */
const OPCIONES_DEL_TIPO = [
  ...(Object.keys(TIPOS_DE_DATO) as Exclude<TipoDeDato, "otro">[]).map((t) => ({ value: t, label: TIPOS_DE_DATO[t].rotulo })),
  { value: "otro", label: "Otro…" },
];

/** Cómo se lee el tipo en la ficha: el rótulo fijo, o el nombre que le puso quien la armó. */
const rotuloDelTipo = (v: TemplateVariable) => {
  const tipo = tipoDeDato(v);
  return tipo === "otro" ? `Otro: ${v.label.trim() || "sin nombre"}` : TIPOS_DE_DATO[tipo].rotulo;
};

const Campo = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <div>
    <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 4 }}>
      {titulo}
    </Text>
    {children}
  </div>
);

/**
 * Ver una plantilla: lo que hoy está publicado en este canal y, si se puede, Eliminar y Editar
 * (xcons-docs#512). Las dos afectan a la definición, o sea a todos los canales de la tienda. Una del
 * catálogo de xcons se edita —la versión nueva es de la tienda y la reemplaza—, pero no se elimina.
 */
const TemplatePanel = ({
  item,
  tienda,
  conectado,
  canEdit,
  ocupado,
  enRevision,
  onRepublicar,
  onEditar,
  onEliminar,
}: {
  item: TemplateItem;
  tienda: string;
  conectado: boolean;
  canEdit: boolean;
  ocupado: string | null;
  /** Una versión más nueva de la misma plantilla, todavía sin reemplazar a ésta. */
  enRevision: TemplateDefinition | null;
  onRepublicar: (pub: TemplatePublication) => void;
  onEditar: () => void;
  onEliminar: () => void;
}) => {
  const { definition: def, publication: pub } = item;
  const estado = estadoDe(item, conectado);
  const detalle = pub?.reason ?? pub?.error ?? null;
  const cat = categoriaDe(pub?.category);
  const aviso = avisoDeCategoria(pub);
  const texto = pub?.renderedBody ?? renderizarTienda(def.body, tienda);
  const publicadas = item.otherChannels.filter((o) => o.status);
  const puedeEliminar = canEdit && !def.catalog;

  return (
    <Flex vertical style={{ flex: 1, minHeight: 0 }}>
      <Flex vertical gap={20} style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        <Campo titulo="Estado en este canal">
          <Flex align="center" gap={8} wrap>
            <Tag color={COLOR_DEL_TONO[estado.tono]} style={{ margin: 0 }}>
              {estado.texto}
            </Tag>
            {canEdit && pub && (pub.status === "error" || pub.status === "pending") && (
              <Button size="small" icon={<ReloadOutlined />} loading={ocupado === pub.publicationId} onClick={() => onRepublicar(pub)}>
                Volver a publicar
              </Button>
            )}
          </Flex>
          {detalle && estado.tono === "problema" && (
            <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
              {detalle}
            </Text>
          )}
        </Campo>
        <Campo titulo="Categoría">
          {cat ? (
            <Space size={4}>
              <Text>{cat.texto}</Text>
              {aviso && (
                <Tooltip title={aviso}>
                  <ExclamationCircleFilled style={{ color: "#fa8c16" }} />
                </Tooltip>
              )}
            </Space>
          ) : (
            <Text type="secondary">—</Text>
          )}
        </Campo>
        <Campo titulo="Mensaje">
          <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "8px 12px", whiteSpace: "pre-wrap" }}>
            {texto}
            {def.footer && (
              <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                {def.footer}
              </Text>
            )}
          </div>
        </Campo>
        {/* Las mismas tarjetas que en el formulario, para leer lo que ahí se escribe (xcons-docs#512). */}
        {def.variables.map((v, i) => (
          <Card
            key={v.n}
            size="small"
            style={TARJETA_CON_ENCABEZADO.style}
            styles={{ header: TARJETA_CON_ENCABEZADO.header, body: { padding: "12px 16px" } }}
            title={<Text style={TARJETA_CON_ENCABEZADO.titulo}>Variable {`{{${v.n}}}`}</Text>}
          >
            <Flex vertical gap={12}>
              <Flex gap={8}>
                <div style={{ flex: 1 }}>
                  <Campo titulo="Tipo de dato">
                    <Text>{rotuloDelTipo(v)}</Text>
                  </Campo>
                </div>
                <div style={{ flex: 1 }}>
                  <Campo titulo="Ejemplo para Meta">
                    <Text>{def.examples[i] || "—"}</Text>
                  </Campo>
                </div>
              </Flex>
            </Flex>
          </Card>
        ))}
        {publicadas.length > 0 && (
          <Campo titulo="Otros canales">
            <Flex vertical gap={2}>
              {publicadas.map((o) => (
                <Text key={o.channelId}>
                  {o.name}: {estadoDe({ ...item, publication: { ...(pub ?? ({} as TemplatePublication)), status: o.status! } }, true).texto}
                </Text>
              ))}
            </Flex>
          </Campo>
        )}
        {def.catalog && (
          <Text type="secondary">
            Plantilla del catálogo de xcons. Si la editás, la versión nueva es de la tienda y reemplaza a ésta en tus canales cuando Meta la
            apruebe.
          </Text>
        )}
        {def.fromCatalog && (
          <Text type="secondary">Versión de la tienda de una plantilla del catálogo de xcons. Si la eliminás, vuelve la del catálogo.</Text>
        )}
      </Flex>
      {canEdit && (
        <div style={{ borderTop: "1px solid #f0f0f0", padding: "12px 24px", flexShrink: 0 }}>
          <Flex justify={puedeEliminar ? "space-between" : "flex-end"} gap={8}>
            {puedeEliminar && (
              <Popconfirm
                title="¿Eliminar esta plantilla?"
                description={`Deja de ofrecerse en todos los canales de la tienda y se borra en Meta.${def.fromCatalog ? " Vuelve la versión del catálogo de xcons." : ""}`}
                okText="Eliminar"
                okButtonProps={{ danger: true }}
                cancelText="Cancelar"
                onConfirm={onEliminar}
              >
                <Button danger icon={<DeleteOutlined />} loading={ocupado === def.definitionId}>
                  Eliminar
                </Button>
              </Popconfirm>
            )}
            {/* Editar una que ya tiene una versión en revisión pisaría esa versión: primero que Meta conteste. */}
            <Tooltip title={enRevision ? `La v${enRevision.version} está en revisión de Meta. Se puede editar cuando Meta conteste.` : null}>
              <Button icon={<EditOutlined />} iconPosition="end" disabled={Boolean(enRevision)} onClick={onEditar}>
                Editar
              </Button>
            </Tooltip>
          </Flex>
        </div>
      )}
    </Flex>
  );
};

/** El alta y la versión nueva: el texto, un ejemplo por variable, y de dónde sale cada una al enviar. */
const TemplateForm = ({
  drawer,
  tienda,
  storeCode,
  channelId,
  onDone,
  onCancel,
}: {
  drawer: Drawer;
  tienda: string;
  /** El Store donde se arma la plantilla: su nombre es el ejemplo para Meta del Store y del Seller. */
  storeCode: string;
  channelId: string;
  onDone: (view: TemplatesView) => void;
  onCancel: () => void;
}) => {
  const inicial = drawer.modo === "version" ? drawer.def : null;
  const [name, setName] = useState("");
  const [body, setBody] = useState(inicial?.body ?? "");
  const [footer, setFooter] = useState(inicial?.footer ?? "");
  const [examples, setExamples] = useState<string[]>(inicial?.examples ?? []);
  // «Variable N» era el nombre por defecto y no le dice nada al operador: al editar, se vuelve a pedir (xcons-docs#512).
  const [variables, setVariables] = useState<TemplateVariable[]>(
    (inicial?.variables ?? []).map((v) => (v.label === `Variable ${v.n}` ? { ...v, label: "" } : v)),
  );
  const [guardando, setGuardando] = useState(false);
  const [tocado, setTocado] = useState(false);

  const numeros = useMemo(() => numerosDeVariables(body), [body]);
  useEffect(() => {
    // Una variable nueva arranca sin nombre: lo escribe quien arma la plantilla.
    setVariables((prev) =>
      variablesPorDefecto(body, prev).map((v) => (prev.some((p) => p.n === v.n) ? v : { ...v, label: "" })),
    );
    setExamples((prev) => numeros.map((_, i) => prev[i] ?? ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body]);

  /**
   * El tipo de cada variable (#519). Sin rótulo todavía no se eligió, salvo que se haya elegido «Otro»
   * y falte escribir qué dato es: eso lo recuerda `otros`.
   */
  const [otros, setOtros] = useState<Set<number>>(new Set());
  const tipos: (TipoDeDato | undefined)[] = variables.map((v) =>
    v.label.trim() ? tipoDeDato(v) : v.source === "contact_name" ? "contact_name" : otros.has(v.n) ? "otro" : undefined,
  );
  // El ejemplo del Store y del Seller es el nombre del Store donde se configura; el fijo, si no carga.
  const { stores } = useOperatorStores();
  const nombreDelStore = stores.find((s) => s.code === storeCode)?.name;
  const ejemploDelTipo = (tipo: Exclude<TipoDeDato, "otro">) =>
    ((tipo === "store_name" || tipo === "seller_name") && nombreDelStore) || TIPOS_DE_DATO[tipo].ejemplo;
  const elegirTipo = (i: number, n: number, tipo: TipoDeDato) => {
    setVariables((prev) => prev.map((v, j) => (j === i ? variableDelTipo(n, tipo) : v)));
    setOtros((prev) => {
      const s = new Set(prev);
      if (tipo === "otro") s.add(n);
      else s.delete(n);
      return s;
    });
    // El ejemplo se propone según el tipo, sin pisar uno que ya escribió quien arma la plantilla. Con
    // «Otro» no hay qué proponer: el propuesto del tipo anterior se borra.
    const propuestos = (Object.keys(TIPOS_DE_DATO) as Exclude<TipoDeDato, "otro">[]).map(ejemploDelTipo);
    const nuevo = tipo === "otro" ? "" : ejemploDelTipo(tipo);
    setExamples((prev) => prev.map((x, j) => (j === i && (!x.trim() || propuestos.includes(x)) ? nuevo : x)));
  };

  const ejemploParaMeta = (i: number) => (
    <Form.Item
      label="Ejemplo para Meta"
      extra="Meta lo pide para revisar la plantilla. El cliente no lo ve."
      style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
    >
      <Input
        placeholder="Ej.: 10234"
        value={examples[i] ?? ""}
        onChange={(e) => setExamples((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))}
      />
    </Form.Item>
  );

  // Una variable sin nombre no es un motivo de Meta: en vez de «no coinciden con el texto», se marca en su
  // campo al intentar enviar (xcons-docs#512). El servicio la sigue rechazando igual.
  const faltaQueDato = variables.some((v) => !v.label.trim());
  const motivos = validarTemplate(
    { name, body, footer, examples, variables: variables.map((v) => (v.label.trim() ? v : { ...v, label: "-" })) },
    { sinNombre: drawer.modo === "version" },
  );
  const preview = renderizar(renderizarTienda(body, tienda), examples);

  // Una edición reemplaza a la versión vigente cuando Meta la aprueba: se avisa antes de mandarla (xcons-docs#512).
  const confirmarYGuardar = () => {
    setTocado(true);
    if (motivos.length || faltaQueDato) return;
    if (drawer.modo !== "version") return void guardar();
    Modal.confirm({
      title: "¿Enviar la edición a revisión de Meta?",
      content: drawer.def.catalog
        ? `Esta plantilla es del catálogo de xcons: la versión nueva pasa a ser de la tienda. Mientras Meta la revisa, los operadores siguen usando la v${drawer.def.version} del catálogo; cuando la apruebe, la v${drawer.def.version} deja de ofrecerse en tus canales.`
        : `Mientras Meta la revisa, los operadores siguen usando la v${drawer.def.version}. Cuando Meta apruebe la versión nueva, la v${drawer.def.version} se elimina.`,
      width: 480,
      icon: <ExclamationCircleOutlined />,
      okText: "Enviar a revisión",
      cancelText: "Cancelar",
      onOk: () => guardar(),
    });
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const texto: TemplateText = { body: body.trim(), footer: footer.trim() || null, examples: examples.map((e) => e.trim()), variables };
      const v =
        drawer.modo === "version"
          ? await conversationsApi.versionTemplate(channelId, drawer.def.definitionId, texto)
          : await conversationsApi.createTemplate(channelId, { name: name.trim(), ...texto });
      onDone(v);
    } catch (e) {
      const data = (e as { response?: { data?: { motivos?: string[]; message?: string } } })?.response?.data;
      if (codigoDeError(e) === "TEMPLATE_INVALID" && data?.motivos?.length) message.error(data.motivos.join(". "));
      else message.error(`No se pudo guardar: ${mensajeDelAlta(e)}`);
    } finally {
      setGuardando(false);
    }
  };

  const enVersion = drawer.modo === "version";
  const mostrarMotivos = (tocado || body.trim().length > 0) && motivos.length > 0;

  return (
    <Flex vertical style={{ flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        <Form layout="vertical">
          {!enVersion && (
            <Form.Item
              label="Nombre"
              extra={
                <>
                  Sirve para identificarla, el cliente no lo ve. Sólo minúsculas, números y guion bajo (_). En Meta
                  aparece con la versión al final, por ejemplo <i>reclamo_seguimiento_v1</i>.
                </>
              }
            >
              <Input value={name} onChange={(e) => setName(e.target.value.toLowerCase())} placeholder="reclamo_seguimiento" maxLength={100} />
            </Form.Item>
          )}
          <Form.Item
            label="Mensaje"
            extra={
              <>
                <div>
                  Usá <Text code>{"{{1}}"}</Text>, <Text code>{"{{2}}"}</Text>… para los datos que cambian en cada envío,
                  y <Text code>{"{tienda}"}</Text> para el nombre del Store.
                </div>
                <div>
                  Para que Meta la apruebe como Utility, tiene que ser el seguimiento de algo que pidió el cliente, sin
                  ofertas ni promociones.
                </div>
              </>
            }
          >
            <Input.TextArea value={body} onChange={(e) => setBody(e.target.value)} rows={4} maxLength={1024} />
            {/* El contador en su propia línea: el de antd pisaba el texto de ayuda. */}
            <Text type="secondary" style={{ display: "block", textAlign: "right", fontSize: 12 }}>
              {body.length} / 1024
            </Text>
          </Form.Item>
          <Form.Item label="Pie de mensaje (opcional)" extra="Texto corto que aparece al final, por ejemplo una firma. Hasta 60 caracteres, sin variables.">
            <Input value={footer} onChange={(e) => setFooter(e.target.value)} maxLength={60} />
          </Form.Item>
          {numeros.map((n, i) => (
            // La tarjeta de siempre, con el encabezado gris (xcons-docs#512).
            <Card
              key={n}
              size="small"
              style={{ ...TARJETA_CON_ENCABEZADO.style, marginBottom: 16 }}
              styles={{ header: TARJETA_CON_ENCABEZADO.header, body: { padding: "12px 16px" } }}
              title={<Text style={TARJETA_CON_ENCABEZADO.titulo}>Variable {`{{${n}}}`}</Text>}
            >
              {/*
                Adentro de la tarjeta los campos van más juntos que en el resto del formulario. El tipo
                ocupa siempre la mitad (#519): con «Otro» queda solo arriba, y abajo qué dato es y su
                ejemplo; con los demás, el ejemplo va al lado.
              */}
              <Flex vertical gap={12}>
                <Flex gap={8}>
                  {/* Un solo campo (#519): el tipo dice qué dato es. */}
                  <Form.Item
                    label="Tipo de dato"
                    validateStatus={tocado && !tipos[i] ? "error" : undefined}
                    help={tocado && !tipos[i] ? "Elegí el tipo de dato" : undefined}
                    style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
                  >
                    <Select
                      placeholder="Elegí el tipo de dato"
                      value={tipos[i]}
                      onChange={(tipo: TipoDeDato) => elegirTipo(i, n, tipo)}
                      options={OPCIONES_DEL_TIPO}
                    />
                  </Form.Item>
                  {tipos[i] === "otro" ? <div style={{ flex: 1 }} /> : ejemploParaMeta(i)}
                </Flex>
                {tipos[i] === "otro" && (
                  <Flex gap={8}>
                    <Form.Item
                      label="Qué dato es"
                      extra="Así lo ve el operador cuando manda la plantilla."
                      validateStatus={tocado && !variables[i]?.label.trim() ? "error" : undefined}
                      help={tocado && !variables[i]?.label.trim() ? "Escribí qué dato es" : undefined}
                      style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Ej.: fecha de entrega"
                        value={variables[i]?.label ?? ""}
                        onChange={(e) => setVariables((prev) => prev.map((v, j) => (j === i ? { ...v, label: e.target.value } : v)))}
                      />
                    </Form.Item>
                    {ejemploParaMeta(i)}
                  </Flex>
                )}
              </Flex>
            </Card>
          ))}
          {body.trim() && (
            <Form.Item label="Así lo ve el cliente">
              <div style={{ background: "#f5f5f5", borderRadius: 8, padding: "8px 12px", whiteSpace: "pre-wrap" }}>
                {preview}
                {footer.trim() && (
                  <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                    {footer}
                  </Text>
                )}
              </div>
            </Form.Item>
          )}
          {mostrarMotivos && (
            <AvisoDeMeta titulo="Meta lo rechazaría así">
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {motivos.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </AvisoDeMeta>
          )}
          {enVersion && (
            <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 12 }}>
              Mientras Meta revisa esta versión, los operadores siguen usando la anterior.
            </Text>
          )}
        </Form>
      </div>
      <div style={{ borderTop: "1px solid #f0f0f0", padding: "12px 24px", flexShrink: 0 }}>
        <Flex justify="space-between" gap={8}>
          <Button icon={<LeftOutlined />} onClick={onCancel}>
            Atrás
          </Button>
          <Button type="primary" loading={guardando} disabled={motivos.length > 0} onClick={confirmarYGuardar}>
            {enVersion ? "Enviar versión a revisión" : "Enviar a revisión de Meta"}
          </Button>
        </Flex>
      </div>
    </Flex>
  );
};
